import Head from 'next/head';
import type { ReactElement } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useApolloClient } from '@apollo/client';
import { Box } from '@zillow/constellation-10';
import { ERROR_MESSAGE } from '@zg-rentals/satellite-components';
import { ErrorPage } from '@zg-rentals/error-page-components';
import Layout from '../../components/Layout';
import StringConstants from '../../constants/StringConstants';
import { AppContext } from '../../contexts/AppContextProvider';
import AuthCheckWrapper from '../../components/AuthCheckWrapper';
import Header from '../../components/Header';
import type { ConversationList_Rh_GetLatestConversationsQuery } from '../../types/GraphqlTypes';
import {
  ConversationList_Rh_GetLatestConversationsDocument,
  Folder,
  ReportConversationReason,
  useInboxPage_Rh_FetchConversationIdLazyQuery,
  useInboxPage_Rh_UpdateConversationStatusMutation,
} from '../../types/GraphqlTypes';
import getFolderFromQueryParam from '../../utils/getFolderFromQueryParam';
import getInboxPageHeadTitle from '../../utils/getInboxPageHeadTitle';
import { InboxPageContextProvider } from '../../contexts/InboxPageContextProvider';
import { getValueFromEnum } from '../../utils/getValueFromEnum';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import useInboxNextRouter from '../../hooks/useInboxNextRouter';
import useGetUserData from '../../hooks/useGetUserData';
import {
  columnWrapperStyles,
  inboxMainContentStyles,
  inboxPageWrapperStyles,
  pageContentStyles,
  primaryColumnStyles,
  secondaryColumnStyles,
} from '../../styles/inboxPageStyle';
import ConfigConstants from '../../constants/ConfigConstants';
import { emitInboxPageViewEvent, INBOX_PAGE_VIEW_EVENT_TYPE_ID } from '../../constants/clickstreamEvents/pageView';
import InboxSkeleton from '../../components/InboxSkeleton';
import ConversationList, { getConversationToRedirectToNext } from '../../components/ConversationList';
import NoConversationsContent from '../../components/NoConversationsContent';
import FullPageNoConversationsWrapper from '../../components/FullPageNoConversationsWrapper';
import WebSocketStatus from '../../components/WebSocketStatus';
import type { UpdateConversationStateArgs } from '../../types/UpdateConversationStateArgs';
import ConversationNav, { ConversationActionTypes } from '../../components/ConversationNav';
import MessageList from '../../components/MessageList';
import { logError } from '@zg-rentals/log-error';

const getVariablesByAction = (action: ConversationActionTypes) => {
  switch (action) {
    case ConversationActionTypes.Archive:
      return { isArchived: true };
    case ConversationActionTypes.Report:
      return { reportReasonType: ReportConversationReason.Spam };
    case ConversationActionTypes.MoveToInbox:
      return { isArchived: false, reportReasonType: ReportConversationReason.NotReported };
    default:
      logError({ error: `No case for: ${action}`, errorType: 'GetVariablesByAction', context: { action } });
      return {};
  }
};

export default function InboxPage() {
  const router = useRouter();
  const { query } = router;
  const conversationId = query.conversationId?.[0];
  const apolloClient = useApolloClient();
  const { folder: folderFromQuery, mobileView } = query;
  const conversationIdValue = conversationId && String(conversationId);
  const folder = getFolderFromQueryParam(folderFromQuery?.toString() || '');
  const [selectedFolder, setSelectedFolder] = useState<Folder>(folder);
  const { isMobileDevice, brand } = useContext(AppContext);
  const { user } = useGetUserData(brand);
  const { userToken, loggedIn } = user || {};
  const { changeRoute } = useInboxNextRouter();
  const [shouldOpenAttachmentsUploader, setShouldOpenAttachmentsUploader] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldFetchOlderConversations, setShouldFetchOlderConversations] = useState(false);
  const [updateConversationStatus] = useInboxPage_Rh_UpdateConversationStatusMutation();

  const handleFolderChange = (selectedOption: Folder) => {
    setSelectedFolder(selectedOption);
    fetchLatestConversationId({
      variables: {
        folder: selectedOption,
        limit: 1,
      },
    });
  };

  const enumValuesFromQueryParams = {
    folder: selectedFolder,
    mobileView: getValueFromEnum(mobileView?.toString(), InboxPageMobileView) || InboxPageMobileView.ConversationList,
  };

  const [fetchLatestConversationId, { data: latestConversationIdData, error }] =
    useInboxPage_Rh_FetchConversationIdLazyQuery({
      errorPolicy: 'all',
      onError: (error) => {
        logError({ error, context: 'Error fetching conversationId to redirect to' });
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      ssr: false,
    });

  // Prevent authentication error by only redirecting
  // logged in users with a token to the latest conversation
  useEffect(() => {
    if (!conversationId && loggedIn && userToken) {
      fetchLatestConversationId({
        variables: {
          limit: 1,
          folder: selectedFolder,
        },
      });
    }
  }, [loggedIn, userToken]);

  useEffect(() => {
    if (latestConversationIdData) {
      setIsLoading(false);
      const { conversationId } = latestConversationIdData.RH_getLatestConversations?.conversations[0] || {};
      changeRoute({
        selectedOption: selectedFolder,
        conversationIdToRedirectTo: conversationId || '',
        isMobile: isMobileDevice,
        mobileViewToChangeTo: enumValuesFromQueryParams.mobileView,
      });
    }
  }, [latestConversationIdData]);

  useEffect(() => {
    if (conversationId || error) {
      setIsLoading(false);
    }
  }, [conversationId, error]);

  // Reset attachments uploader state when conversation changes
  useEffect(() => {
    setShouldOpenAttachmentsUploader(false);
  }, [conversationId]);

  const handleSecondaryColumnDragEnter = () => setShouldOpenAttachmentsUploader(true);
  const handleSecondaryColumnDragLeave = () => setShouldOpenAttachmentsUploader(false);

  useEffect(() => {
    setShouldOpenAttachmentsUploader(false);
    window.addEventListener('dragenter', handleSecondaryColumnDragEnter);
    window.addEventListener('dragleave', handleSecondaryColumnDragLeave);

    return () => {
      window.removeEventListener('dragenter', handleSecondaryColumnDragEnter);
      window.removeEventListener('dragleave', handleSecondaryColumnDragLeave);
    };
  }, []);

  useEffect(() => {
    emitInboxPageViewEvent();
  }, []);

  const updateConversationStatusByAction = async (conversationId: string, action: ConversationActionTypes) => {
    try {
      const response = await updateConversationStatus({
        variables: {
          conversationId,
          ...getVariablesByAction(action),
        },
      });

      if (!response.data?.RH_updateConversationStatusV2.success) {
        throw new Error();
      }
    } catch (err) {
      logError({ error: err, context: { conversationId, info: `Error on ${action} conversation` } });
    }
  };

  const showMessageListOnMobile = () => {
    changeRoute({
      conversationIdToRedirectTo: conversationId,
      mobileViewToChangeTo: InboxPageMobileView.MessageList,
      isMobile: isMobileDevice,
    });
  };

  const showConversationListOnMobile = () => {
    changeRoute({
      conversationIdToRedirectTo: conversationId,
      mobileViewToChangeTo: InboxPageMobileView.ConversationList,
      isMobile: isMobileDevice,
    });
  };

  const handleUndoClick = async (conversationIdToUndo: string) => {
    if (!conversationIdToUndo) return;

    const updateConversationStatus = () => {
      switch (selectedFolder) {
        case Folder.Archived:
          return updateConversationStatusByAction(conversationIdToUndo, ConversationActionTypes.Archive);
        case Folder.Spam:
          return updateConversationStatusByAction(conversationIdToUndo, ConversationActionTypes.Report);
        case Folder.Inbox:
          return updateConversationStatusByAction(conversationIdToUndo, ConversationActionTypes.MoveToInbox);
        default:
          logError({
            error: `No case for undo click: ${selectedFolder}`,
            errorType: 'StatusUpdateError',
            context: { conversationIdToUndo },
          });
      }
    };

    await updateConversationStatus();

    changeRoute({
      selectedOption: selectedFolder,
      conversationIdToRedirectTo: conversationIdToUndo,
      isMobile: isMobileDevice,
    });
    refetchConversationList({ conversationIdToRedirectTo: conversationIdToUndo });
  };

  const refetchConversationList = async ({ conversationIdToRedirectTo }: { conversationIdToRedirectTo?: string }) => {
    const daysBack = shouldFetchOlderConversations ? ConfigConstants.MAX_DAYS_BACK_FOR_CONVERSATIONS : null;

    try {
      const cachedData = apolloClient.cache.readQuery<ConversationList_Rh_GetLatestConversationsQuery>({
        query: ConversationList_Rh_GetLatestConversationsDocument,
        variables: {
          daysBack,
          folder: selectedFolder,
        },
      });

      await apolloClient.query<ConversationList_Rh_GetLatestConversationsQuery>({
        query: ConversationList_Rh_GetLatestConversationsDocument,
        variables: {
          daysBack,
          folder: selectedFolder,
        },
        fetchPolicy: 'network-only',
      });

      const cachedConversations = cachedData?.RH_getLatestConversations.conversations || [];
      const nextConversationId =
        conversationIdToRedirectTo || getConversationToRedirectToNext(conversationId!, cachedConversations);

      if (nextConversationId) {
        updateConversationState({ conversationId: nextConversationId });
      }
    } catch (err) {
      logError({ error: err, context: 'Error refetching conversations' });
    }
  };

  const updateConversationState = ({
    conversationId,
    hasLoadedOlderConversations,
    showConversationList = true,
    shouldChangeRoute = true,
  }: UpdateConversationStateArgs): void => {
    if (hasLoadedOlderConversations) {
      setShouldFetchOlderConversations(hasLoadedOlderConversations);
    }

    if (shouldChangeRoute) {
      changeRoute({
        conversationIdToRedirectTo: conversationId,
        mobileViewToChangeTo: showConversationList
          ? InboxPageMobileView.ConversationList
          : InboxPageMobileView.MessageList,
        isMobile: isMobileDevice,
      });
    }
  };

  const errorObj = error?.graphQLErrors[0];
  const { code } = errorObj?.extensions || {};
  if (error) {
    if (code === StringConstants.GRAPHQL_ERROR_TYPES.BAD_REQUEST) {
      return (
        <ErrorPage
          errorMessage={ERROR_MESSAGE}
          errorDetails="There was an error loading this conversation."
          action={<a href={StringConstants.BASE_PATH}>Return to Inbox</a>}
        />
      );
    }
  }

  const shouldHideConversationListOnMobile =
    enumValuesFromQueryParams.mobileView !== InboxPageMobileView.ConversationList;
  const shouldShowNoConversationsUI =
    !error && !isLoading && !latestConversationIdData?.RH_getLatestConversations.conversations.length;

  return (
    <InboxPageContextProvider
      value={{
        conversationId: conversationIdValue,
        folder: enumValuesFromQueryParams.folder,
        mobileView: enumValuesFromQueryParams.mobileView,
      }}
    >
      <WebSocketStatus />

      <Header onFolderChange={handleFolderChange} />

      {!conversationId && (
        <>
          {isLoading && <InboxSkeleton shouldHideConversationListOnMobile={shouldHideConversationListOnMobile} />}

          {error && !isLoading && (
            <FullPageNoConversationsWrapper>
              <NoConversationsContent errorCode={code} showErrorExperience={true} />
            </FullPageNoConversationsWrapper>
          )}

          {shouldShowNoConversationsUI && (
            <FullPageNoConversationsWrapper>
              <NoConversationsContent updateConversationState={updateConversationState} showErrorExperience={false} />
            </FullPageNoConversationsWrapper>
          )}
        </>
      )}

      {conversationId && (
        <Box css={columnWrapperStyles}>
          <Box
            css={{
              ...primaryColumnStyles,
              display: shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
            }}
          >
            <ConversationList
              showMessageListOnMobile={showMessageListOnMobile}
              updateConversationState={updateConversationState}
              shouldFetchOlderConversations={shouldFetchOlderConversations}
            />
          </Box>
          <Box
            css={{
              ...secondaryColumnStyles,
              display: !shouldHideConversationListOnMobile ? { base: 'none', xl: 'flex' } : 'flex',
            }}
            onDragEnter={handleSecondaryColumnDragEnter}
            onDragLeave={handleSecondaryColumnDragLeave}
          >
            <ConversationNav
              refetchConversationList={refetchConversationList}
              showConversationListOnMobile={showConversationListOnMobile}
              onToastUndoClick={handleUndoClick}
            />
            <MessageList
              shouldAllowSendingAttachmentsOnly
              shouldOpenAttachmentsUploader={shouldOpenAttachmentsUploader}
              shouldFetchOlderConversations={shouldFetchOlderConversations}
            />
          </Box>
        </Box>
      )}
    </InboxPageContextProvider>
  );
}

InboxPage.getLayout = function getLayout(page: ReactElement) {
  const brandName = page.props.brand;
  const pageHeadTitle = getInboxPageHeadTitle(brandName);

  return (
    <AuthCheckWrapper requireStrongAuthentication={false}>
      <Layout mainContentStyles={inboxMainContentStyles} pageWrapperStyles={inboxPageWrapperStyles}>
        <>
          <Head>
            <title>{pageHeadTitle}</title>
          </Head>
          <Box css={pageContentStyles} data-eventid={INBOX_PAGE_VIEW_EVENT_TYPE_ID} data-eventdescription="Page view">
            {page}
          </Box>
        </>
      </Layout>
    </AuthCheckWrapper>
  );
};
